import { serverEnv } from './env'
import { centsToEuro } from './sla'

type Mail = { to: string; subject: string; text: string }

// Resend when a key is configured, otherwise the message is logged. That keeps local work and
// tests out of real inboxes without a second code path for "is this a test".
async function send(mail: Mail): Promise<'sent' | 'logged' | 'failed'> {
  const env = serverEnv()
  const from = env.MAIL_FROM
  if (!env.RESEND_API_KEY || !from) {
    console.info('[mail:logged]', mail.subject, '->', mail.to, '\n', mail.text)
    return 'logged'
  }
  try {
    const response = await fetch('https://api.resend.com/emails', {
      method: 'POST',
      headers: {
        authorization: `Bearer ${env.RESEND_API_KEY}`,
        'content-type': 'application/json',
      },
      body: JSON.stringify({ from, to: [mail.to], subject: mail.subject, text: mail.text }),
    })
    if (!response.ok) {
      console.error('[mail:failed]', response.status, await response.text())
      return 'failed'
    }
    return 'sent'
  } catch (error) {
    console.error('[mail:failed]', error)
    return 'failed'
  }
}

async function push(title: string, body: string) {
  const env = serverEnv()
  if (!env.NTFY_TOPIC) return
  try {
    await fetch(`https://ntfy.sh/${env.NTFY_TOPIC}`, {
      method: 'POST',
      headers: { title, priority: 'default' },
      body,
    })
  } catch (error) {
    // a missing push must never fail the request - the email is the channel that counts
    console.error('[ntfy:failed]', error)
  }
}

// Every founder alert: email when FOUNDER_EMAIL is set, otherwise logged; ntfy on top if configured.
async function alertFounder(subject: string, pushTitle: string, text: string) {
  const env = serverEnv()
  let mail: Awaited<ReturnType<typeof send>> = 'logged'
  if (env.FOUNDER_EMAIL) {
    mail = await send({ to: env.FOUNDER_EMAIL, subject, text })
  } else {
    console.info('[alert:logged] FOUNDER_EMAIL is unset:', subject, '\n', text)
  }
  await push(pushTitle, text)
  return mail
}

export type RequestSummary = {
  id: string
  bookerName: string
  bookerEmail: string
  occasion: string
  city: string
  eventDate: Date
  budgetMinCents: number | null
  budgetMaxCents: number | null
  slaDueAt: Date
}

// Vercel runs in UTC; without the zone a deadline in an email is one or two hours off
const timeZone = 'Europe/Berlin'
const de = (date: Date) => date.toLocaleDateString('de-DE', { dateStyle: 'medium', timeZone })
const deTime = (date: Date) =>
  date.toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short', timeZone })

export async function notifyFounder(request: RequestSummary) {
  const env = serverEnv()
  const budget =
    request.budgetMinCents === null && request.budgetMaxCents === null
      ? 'kein Budget angegeben'
      : `${request.budgetMinCents === null ? '?' : centsToEuro(request.budgetMinCents)} bis ${
          request.budgetMaxCents === null ? '?' : centsToEuro(request.budgetMaxCents)
        }`
  const link = `${env.NEXT_PUBLIC_SITE_URL}/admin/requests/${request.id}`
  const text = [
    `Neue Anfrage von ${request.bookerName} (${request.bookerEmail})`,
    `Anlass: ${request.occasion}`,
    `Termin: ${de(request.eventDate)} in ${request.city}`,
    `Budget: ${budget}`,
    `Antwort faellig bis: ${deTime(request.slaDueAt)}`,
    '',
    link,
  ].join('\n')

  const subject = `Neue Anfrage: ${request.occasion}`
  return alertFounder(subject, 'Neue Anfrage', text)
}

export type BandSummary = {
  id: string
  name: string
  contactName: string
  email: string
  region: string
  lineupType: string
  priceFromCents: number
  videoUrl: string | null
  hasOffer: boolean
}

// Band signup (S0-013/S0-014): one alert per new band, same channels as a new request.
export async function notifyFounderOfBand(band: BandSummary) {
  const env = serverEnv()
  const text = [
    `Neue Band: ${band.name} (${band.contactName}, ${band.email})`,
    `Region: ${band.region}`,
    `Besetzung: ${band.lineupType}, ab ${centsToEuro(band.priceFromCents)}`,
    `Video: ${band.videoUrl ?? 'keins angegeben'}`,
    `Gig eingetragen: ${band.hasOffer ? 'ja' : 'nein'}`,
    '',
    // shortcut: no band detail page in /admin yet, link the overview until one exists
    `${env.NEXT_PUBLIC_SITE_URL}/admin`,
  ].join('\n')

  const subject = `Neue Band: ${band.name}`
  return alertFounder(subject, 'Neue Band', text)
}

// Band signup confirmation (S0-013). Bands are addressed with "ihr", like the signup form.
export async function sendBandConfirmation(band: BandSummary) {
  return send({
    to: band.email,
    subject: 'Eure Anmeldung bei ensemble ist angekommen',
    text: [
      `Hallo ${band.contactName},`,
      '',
      `danke, dass ${band.name} bei ensemble dabei sein möchte.`,
      '',
      band.hasOffer
        ? 'Euren eingetragenen Gig schauen wir uns an und nehmen ihn ins Angebot auf, sobald er passt.'
        : 'Sobald es eine Anfrage gibt, die zu euch passt, melden wir uns bei euch.',
      'Ihr bekommt von uns nur Anfragen, die zu Region, Besetzung und Preis passen.',
      '',
      'Wenn sich etwas an euren Angaben ändert, antwortet einfach auf diese E-Mail.',
      '',
      'Viele Grüße',
      'ensemble',
    ].join('\n'),
  })
}

export async function sendBookerConfirmation(request: RequestSummary) {
  return send({
    to: request.bookerEmail,
    subject: 'Deine Anfrage bei ensemble ist angekommen',
    text: [
      `Hallo ${request.bookerName},`,
      '',
      `danke für deine Anfrage für ${request.occasion} am ${de(request.eventDate)} in ${request.city}.`,
      '',
      'So geht es weiter: Wir suchen passende Bands und melden uns innerhalb von 48 Stunden mit bis',
      'zu drei Angeboten bei dir. Du entscheidest dann in Ruhe.',
      '',
      'Wenn sich etwas an deinen Angaben ändert, antworte einfach auf diese E-Mail.',
      '',
      'Viele Grüße',
      'ensemble',
    ].join('\n'),
  })
}

// Admin login (S0-015). German, informal "du" - the only recipient is the founder.
export async function sendAdminMagicLink(to: string, url: string) {
  return send({
    to,
    subject: 'Dein Login-Link für ensemble',
    text: [
      'Hier ist dein Login-Link für den ensemble-Adminbereich:',
      '',
      url,
      '',
      'Der Link gilt 10 Minuten und funktioniert einmal.',
      'Wenn du dich nicht anmelden wolltest, ignoriere diese Mail einfach.',
    ].join('\n'),
  })
}

// Offer composer (S0-018): the booker gets the status link, nothing else. The plaintext token
// exists only in this email - the database stores its hash.
export async function sendOffersToBooker(
  request: Pick<RequestSummary, 'bookerName' | 'bookerEmail' | 'occasion' | 'eventDate' | 'city'>,
  statusUrl: string,
  count: number,
  validUntil: Date
) {
  return send({
    to: request.bookerEmail,
    subject:
      count === 1
        ? 'Ein Angebot für deine Anfrage ist da'
        : `${count} Angebote für deine Anfrage sind da`,
    text: [
      `Hallo ${request.bookerName},`,
      '',
      `für ${request.occasion} am ${de(request.eventDate)} in ${request.city} haben wir ${
        count === 1 ? 'eine passende Band' : `${count} passende Bands`
      } für dich gefunden.`,
      '',
      'Preis, Leistungen und Video findest du hier - dort kannst du auch direkt zusagen oder absagen:',
      statusUrl,
      '',
      `Der Link gilt bis ${de(validUntil)} und ist nur für dich. Bitte leite ihn nicht weiter.`,
      '',
      'Viele Grüße',
      'ensemble',
    ].join('\n'),
  })
}

export type OfferAnswer = {
  requestId: string
  bookerName: string
  occasion: string
  eventDate: Date
  bandName: string
  priceCents: number
  booking: { feeCents: number; invoiceRef: string } | null
  decision: 'accept' | 'decline'
}

export async function notifyFounderOfAnswer(answer: OfferAnswer) {
  const env = serverEnv()
  const verb = answer.decision === 'accept' ? 'angenommen' : 'abgelehnt'
  const subject = `Angebot ${verb}: ${answer.bandName}`
  const text = [
    `${answer.bookerName} hat das Angebot von ${answer.bandName} (${centsToEuro(answer.priceCents)}) ${verb}.`,
    `Anfrage: ${answer.occasion} am ${de(answer.eventDate)}`,
    ...(answer.booking
      ? [
          '',
          'Gebucht. Die anderen Angebote dieser Anfrage sind damit abgelehnt.',
          `Gage: ${centsToEuro(answer.priceCents)}`,
          `Servicegebühr: ${centsToEuro(answer.booking.feeCents)}`,
          `Rechnungsnummer: ${answer.booking.invoiceRef}`,
          'Jetzt Rechnung und Vertrag von Hand schicken - es läuft keine Zahlung über ensemble.',
        ]
      : []),
    '',
    `${env.NEXT_PUBLIC_SITE_URL}/admin/requests/${answer.requestId}`,
  ].join('\n')
  return alertFounder(subject, `Angebot ${verb}`, text)
}

const slaSubjects = {
  stale: 'Seit 24 h unbearbeitet',
  due_soon: 'Frist endet in 6 h',
  missed: 'Frist verpasst',
} as const

// The SLA cron (S0-020): one reminder per request and stage, deduplicated by the caller.
export async function notifyFounderOfSla(
  request: Pick<
    RequestSummary,
    'id' | 'bookerName' | 'occasion' | 'city' | 'eventDate' | 'slaDueAt'
  >,
  stage: keyof typeof slaSubjects
) {
  const env = serverEnv()
  const subject = `${slaSubjects[stage]}: ${request.occasion}, ${request.bookerName}`
  const text = [
    `${slaSubjects[stage]}. Frist der 48-Stunden-Zusage an ${request.bookerName}: ${deTime(request.slaDueAt)}`,
    `Anfrage: ${request.occasion} am ${de(request.eventDate)} in ${request.city}`,
    '',
    `${env.NEXT_PUBLIC_SITE_URL}/admin/requests/${request.id}`,
  ].join('\n')
  return alertFounder(subject, slaSubjects[stage], text)
}
